import React, { useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Eye, ArrowUpDown } from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Sortable column definitions for leftover inventory
const SORTABLE_COLUMNS = ['tip_diameter', 'length', 'quantity', 'created_at'];

const LeftoverInventoryList = React.memo(({
  inventory,
  loading,
  sort,
  onSort,
  onClearAllFilters,
  debouncedGlobalSearch,
  onViewLeftover,
  selectedClientId = null
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const headCells = [
    { id: 'code', label: '余料编码', sortable: false },
    { id: 'parent_product', label: '父产品', sortable: false },
    { id: 'tip_diameter', label: '直径 (mm)', sortable: true },
    { id: 'length', label: '长度 (mm)', sortable: true },
    { id: 'quantity', label: '数量', sortable: true },
    { id: 'location', label: '位置', sortable: false },
    { id: 'status', label: '状态', sortable: false },
    { id: 'actions', label: '操作', sortable: false, width: 120 }
  ];

  const handleSort = useCallback((columnId) => {
    if (!SORTABLE_COLUMNS.includes(columnId)) return;
    onSort(columnId);
  }, [onSort]);

  // Mobile view
  if (isMobile) {
    return (
      <div>
        <div className="flex justify-between items-center mb-4 p-4 bg-muted rounded border border-gray-200">
          <span className="text-sm text-muted-foreground">
            排序方式: {headCells.find(h => h.id === sort.by)?.label || '编码'} ({sort.order === 'asc' ? '升序' : '降序'})
          </span>
          <Button size="sm" variant="outline" onClick={() => handleSort(sort.by)}>
            <ArrowUpDown className="h-4 w-4 mr-1" />
            切换排序
          </Button>
        </div>

        <div className="space-y-0" role="list" aria-label="余料库存列表">
          {inventory.map((item, index) => (
            <div key={item._id ? String(item._id) : `lo-${index}`} role="listitem">
              <Card className="p-4 mb-2 border-b rounded-none hover:bg-muted/50">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-sm">{item.code || '无编码'}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.parent_product_name || item.parent_product_code || '-'}
                    </p>
                  </div>
                  <Badge variant={item.status === 'available' ? 'default' : 'secondary'}>
                    {item.status === 'available' ? '可用' : item.status}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                  <div>
                    <span className="text-muted-foreground">直径:</span>
                    <span className="ml-1 font-medium">{item.tip_diameter || '-'} mm</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">长度:</span>
                    <span className="ml-1 font-medium">{item.length || '-'} mm</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">数量:</span>
                    <span className="ml-1 font-medium">{item.quantity || 0}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    位置: {item.location || '-'}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onViewLeftover?.(item)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    查看
                  </Button>
                </div>
              </Card>
            </div>
          ))}
        </div>

        {inventory.length === 0 && (
          <Card className="p-8 text-center mt-4">
            <p className="text-muted-foreground mb-4">
              {debouncedGlobalSearch?.trim()
                ? `未找到匹配 "${debouncedGlobalSearch}" 的余料`
                : '暂无余料库存'}
            </p>
            <Button size="sm" onClick={onClearAllFilters}>
              清除筛选
            </Button>
          </Card>
        )}
      </div>
    );
  }

  // Desktop table view
  return (
    <div>
      <Card className="shadow-none">
        <div className="overflow-auto">
          <table className="w-full" aria-label="余料库存列表">
            <thead className="bg-muted sticky top-0">
              <tr>
                {headCells.map((headCell) => (
                  <th
                    key={headCell.id}
                    className={`px-4 py-3 text-left text-sm font-semibold text-foreground ${
                      headCell.width ? `w-[${headCell.width}px]` : ''
                    }`}
                  >
                    {headCell.sortable ? (
                      <button
                        className="flex items-center gap-1 hover:text-primary"
                        onClick={() => handleSort(headCell.id)}
                      >
                        {headCell.label}
                        <ArrowUpDown className={`h-4 w-4 ${sort.by === headCell.id ? 'text-primary' : 'text-muted-foreground'}`} />
                      </button>
                    ) : (
                      headCell.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {inventory.map((item, index) => {
                const itemId = item._id || item.code || `lo-${index}`;

                return (
                  <tr key={itemId} className="hover:bg-muted">
                    {/* Code */}
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono font-medium">
                        {item.code || '-'}
                      </span>
                    </td>

                    {/* Parent Product */}
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <span className="font-medium">{item.parent_product_name || '-'}</span>
                        {item.parent_product_code && (
                          <p className="text-xs text-muted-foreground">{item.parent_product_code}</p>
                        )}
                      </div>
                    </td>

                    {/* Tip Diameter */}
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {item.tip_diameter !== undefined ? `${item.tip_diameter} mm` : '-'}
                      </span>
                    </td>

                    {/* Length */}
                    <td className="px-4 py-3">
                      <span className="text-sm">
                        {item.length !== undefined ? `${item.length} mm` : '-'}
                      </span>
                    </td>

                    {/* Quantity */}
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium">
                        {item.quantity || 0}
                      </span>
                    </td>

                    {/* Location */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground">
                        {item.location || '-'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <Badge
                        variant={item.status === 'available' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {item.status === 'available' ? '可用' : item.status}
                      </Badge>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onViewLeftover?.(item)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        查看
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {inventory.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {debouncedGlobalSearch?.trim()
                ? `未找到匹配 "${debouncedGlobalSearch}" 的余料`
                : '暂无余料库存'}
            </p>
            <Button size="sm" onClick={onClearAllFilters}>
              清除筛选
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
});

LeftoverInventoryList.displayName = 'LeftoverInventoryList';

export default LeftoverInventoryList;
